import "../styles/default.scss";

import autoBind from "auto-bind";
import classNames from "classnames";
import { isEmpty } from "lodash";
import qs from "qs";
import React from "react";
import { Redirect } from "react-router";
import { compose } from "redux";

import {
  getLoginWithRedirectUrl,
  getRegisterWithRedirectUrl
} from "../../../../../lib/auth/authRedirectUrl";
import { getOfferDays } from "../../../../../lib/Plans";
import paylaneLogo from "../../../../../assets/images/payments/paylane.png";
import payuLogo from "../../../../../assets/images/payments/payu.png";
import stripeLogo from "../../../../../assets/images/payments/stripe.png";
import stripeSubscriptionLogo from "../../../../../assets/images/payments/stripe_subscription.png";
import Price from "../../../../../common/Price";
import config from "../../../../../config";
import withAddAlert from "../../../../../hocs/withAddAlert";
import withGlobalConfigFields from "../../../../../hocs/withGlobalConfigFields";
import withPaymentDetails from "../../../../../hocs/withPaymentDetails";
import withPaymentForm from "../../../../../hocs/withPaymentForm";
import withUserData from "../../../../../hocs/withUserData";
import { globalConfigFields } from "../../../../../redux/modules/globalConfig";
import ContentSection from "../../../common/ContentSection";
import Loader from "../../../common/Loader";
import PageTitle from "../../../common/PageTitle";
import SquaberLink from "../../../common/SquaberLink";
import Bank from "../../../landingPage/components/LandingTypography/assets/bank.svg";
import Blik from "../../../landingPage/components/LandingTypography/assets/blik.png";
import Mastercard from "../../../landingPage/components/LandingTypography/assets/mastercard.png";
import Visa from "../../../landingPage/components/LandingTypography/assets/visa.png";
import Confirmation from "../components/Confirmation";
import getDiscountOffer from "../utils/getDiscountOffer";
import getOfferDescription from "../utils/getOfferDescription";
import extractOfferPaymentMethods from "../utils/extractPaymentMethodsFromOffer";
import hasDescription from "../utils/hasDescription";
import hasDiscount from "../utils/hasDiscount";
import PaymentBarTooltip from "../../../landingPage/pages/Plans/components/PaymentBarTooltip/PaymentBarTooltip";
import { getPaymentMethodPricing } from "../../../landingPage/pages/Plans/utils/useOnPaymentButtonClick";
import FormatCurrency from "../../../landingPage/pages/Homepage/components/FormatCurrency";
import withPlans from "../../../../../hocs/withPlans";

export interface PaymentMethodMetaData {
  paymentMethods: Array<string>;
  subscriptionId: Number | null;
}

export const PAYMENT_METHODS = {
  PAYLANE: "PayLane",
  PAYPAL: "PayPal",
  PAYU: "PayU",
  VISA: "VISA",
  STRIPE: "Stripe",
  STRIPE_SUBSCRIPTION: "Stripe Subscription"
};

export const CARD_MODEL = {
  card_code: "",
  card_number: "",
  expiration_month: "01",
  expiration_year: "18",
  name_on_card: ""
};

export const SUPPORTED_PAYMENT_METHODS = [
  {
    name: PAYMENT_METHODS.PAYLANE,
    image: paylaneLogo,
    alias: "PAYLANE",
    canDiscount: true,
    requiresSubscriptionId: false
  },
  {
    name: PAYMENT_METHODS.PAYU,
    image: payuLogo,
    alias: "PAYU",
    canDiscount: true,
    requiresSubscriptionId: false,
    children: (
      <>
        <img src={Blik} alt={"Blik"} height={10} />
        <img src={Bank} alt={"Bank transfer"} width={10} height={10} />
      </>
    )
  },
  {
    name: PAYMENT_METHODS.STRIPE,
    image: stripeLogo,
    alias: "Stripe",
    canDiscount: true,
    requiresSubscriptionId: false,
    isDefault: true,
    children: (
      <>
        <img src={Mastercard} alt={"Mastercard"} height={10} />
        <img src={Visa} alt={"Visa"} height={10} />
      </>
    )
  },
  {
    name: PAYMENT_METHODS.STRIPE_SUBSCRIPTION,
    image: stripeSubscriptionLogo,
    alias: "Stripe Subscription",
    canDiscount: false,
    requiresSubscriptionId: true,
    children: (
      <>
        <img src={Visa} alt={"Visa"} height={10} />
        <img src={Mastercard} alt={"Mastercard"} height={10} />
      </>
    )
  }
];

export class PaymentForm extends React.Component {
  constructor(props) {
    super(props);

    autoBind.react(this);
  }

  state = {
    currentPaymentMethod: null,
    paymentMethod: null,
    paymentMethodAlias: null,
    agreementAccepted: false,
    cardModel: CARD_MODEL
  };

  paymentMethods = SUPPORTED_PAYMENT_METHODS;

  componentDidMount() {
    this.props.fetchPaymentDetails();
    this.props.fetchOffers();
  }

  setAgreementAccepted(value) {
    this.setState({ agreementAccepted: value });
  }

  setPaymentMethod(method) {
    this.setState({
      paymentMethod: method.name,
      paymentMethodAlias: method.alias,
      currentPaymentMethod: method
    });
  }

  setCardModelValue(key, value) {
    const { cardModel } = this.state;

    cardModel[key] = value;

    this.setState({ cardModel });
  }

  getUserIsLoggedIn() {
    const {
      userData,
      location: { search }
    } = this.props;

    const searchParams = qs.parse(search, { ignoreQueryPrefix: true });

    return !!userData || !!searchParams.auth_token_for_login;
  }

  getDiscountSummary(selectedOffer) {
    const { translate, paymentDetails } = this.props;
    const { currency } = paymentDetails;

    const discountedOffer = getDiscountOffer({
      ...selectedOffer
    });

    return (
      <>
        <span className="label">{translate("Discount")}:</span>
        <span className="offer-detail">
          -
          <Price
            value={Number(discountedOffer.usedAffiliatePoints)}
            currency={currency}
          />
        </span>
        <span className="label">{`${translate("Price after discount")}`}:</span>
        <span className="offer-detail">
          <Price
            value={Number(discountedOffer.amountAfterDiscount)}
            currency={currency}
          />{" "}
          {selectedOffer.periods !== 1 ? (
            <span>
              {"("}
              <Price
                value={
                  Number(discountedOffer.amountAfterDiscount) /
                  selectedOffer.periods
                }
                currency={currency}
                roundToSecondNumber
              />{" "}
              / {translate("month")}
              {")"}
            </span>
          ) : null}
        </span>
      </>
    );
  }

  render() {
    const {
      translate,
      locale,
      match: {
        params: { offerId }
      },
      selectedOffer,
      selectedOfferError,
      paymentDetails,
      paymentFormLoading,
      addInfoAlert,
      finalizePayment,
      globalConfig: { offerUrl },
      userWasLoggedIn,
      offers
    } = this.props;

    const { agreementAccepted } = this.state;

    const userIsLoggedIn = this.getUserIsLoggedIn();

    if (!userIsLoggedIn) {
      const getRedirectUrl = userWasLoggedIn
        ? getLoginWithRedirectUrl
        : getRegisterWithRedirectUrl;

      const infoAlertContent = userWasLoggedIn
        ? "You have to log in to continue"
        : "You have to register to continue";

      addInfoAlert({
        title: translate(infoAlertContent)
      });

      return <Redirect to={getRedirectUrl({ locale })} />;
    }

    const {
      cardModel,
      paymentMethod,
      paymentMethodAlias,
      currentPaymentMethod
    } = this.state;

    const { currency } = paymentDetails;

    const isEmptySelectedOffer = !selectedOffer || isEmpty(selectedOffer);

    if (selectedOfferError) {
      // This logic is derived from offerOutdated check
      setTimeout(() => {
        window.open(offerUrl || config.defaultPremiumOfferUrl, "_self");
      }, 3000);

      return (
        <ContentSection>
          <p>
            <SquaberLink to={offerUrl || config.defaultPremiumOfferUrl}>
              {translate(
                "The offer is no longer available, take advantage of current offers"
              )}
            </SquaberLink>
          </p>
        </ContentSection>
      );
    }

    if (isEmptySelectedOffer) {
      return <Loader />;
    }

    let isRecurringPaymentMethod =
      [
        PAYMENT_METHODS.VISA,
        PAYMENT_METHODS.PAYLANE,
        PAYMENT_METHODS.PAYPAL,
        PAYMENT_METHODS.STRIPE_SUBSCRIPTION
      ].indexOf(paymentMethod) !== -1;

    const {
      paymentMethods: offerPaymentMethods,
      subscriptionId
    } = extractOfferPaymentMethods(selectedOffer);

    const discounted = hasDiscount(selectedOffer);

    const offerDescription = selectedOffer?.description;
    const specialOfferDescription = selectedOffer?.special_offer_description;

    const hasOfferDescription = hasDescription(offerDescription);
    const hasSpecialOfferDescription = hasDescription(specialOfferDescription);

    const paymentMethodPricingBase = getPaymentMethodPricing({
      selectedPaymentMethod: paymentMethod,
      currentCurrencyOfferByPeriod: selectedOffer
    });

    const paymentMethodPricing = isEmpty(paymentMethodPricingBase)
      ? null
      : paymentMethodPricingBase;

    const selectedPlanLoading = offers?.loading;

    const selectedPlan = offers?.data?.offers?.[0]?.plans?.find(
      plan => plan.name === selectedOffer?.plan_name
    );

    return (
      <div className="payment-form">
        <PageTitle title={translate("Payment")} />
        <div className={"wrapper"}>
          <ContentSection>
            <div className="selected-offer-details-wrapper">
              <Confirmation
                selectedOffer={selectedOffer}
                selectedPlanLoading={selectedPlanLoading}
                selectedPlan={selectedPlan}
                agreementAccepted={agreementAccepted}
                setAgreementAccepted={this.setAgreementAccepted}
                paymentMethodPricing={paymentMethodPricing}
                offerPaymentMethods={offerPaymentMethods}
                paymentMethods={this.paymentMethods}
                paymentMethod={paymentMethod}
                paymentMethodAlias={paymentMethodAlias}
                currentPaymentMethod={currentPaymentMethod}
                paymentDetails={paymentDetails}
                setPaymentMethod={this.setPaymentMethod}
                isRecurringPaymentMethod={isRecurringPaymentMethod}
                currency={currency}
                paymentFormLoading={paymentFormLoading}
                cardModel={cardModel}
                offerId={offerId}
                finalizePayment={finalizePayment}
                subscriptionId={subscriptionId}
                small
                isSideButton
              />
            </div>
            <div className="summary">
              <h1>{translate("Summary")}</h1>
              <div className="selected-offer-details">
                <span className="label">{translate("Selected option")}:</span>
                <span className="offer-detail">
                  {selectedOffer?.plan_name
                    ? translate("%{plan_name} plan for %{days} days", {
                        plan_name: selectedOffer?.plan_name,
                        days: getOfferDays(selectedOffer)
                      })
                    : translate("Squaber for %{days} days", {
                        days: getOfferDays(selectedOffer)
                      })}
                </span>
                <span className="label">{translate("Price")}:</span>
                <span
                  className={classNames("offer-detail", {
                    discounted
                  })}
                >
                  {FormatCurrency({
                    value: Number(selectedOffer.offer_amount),
                    locale: locale
                  })}{" "}
                  {selectedOffer.periods !== 1 ? (
                    <span>
                      {"("}
                      {FormatCurrency({
                        value: paymentMethodPricing
                          ? Number(paymentMethodPricing.monthly_amount)
                          : Number(selectedOffer.order_amount) /
                            selectedOffer.periods,
                        locale: locale
                      })}{" "}
                      / {translate("month")}
                      {")"}
                    </span>
                  ) : null}
                </span>
                {paymentMethodPricing ? (
                  <>
                    <PaymentBarTooltip
                      prorationAmount={paymentMethodPricing?.proration_amount}
                      affiliationDiscountAmount={
                        paymentMethodPricing?.affiliation_discount_amount
                      }
                      discountAmount={paymentMethodPricing?.discount_amount}
                      subtractedAmount={paymentMethodPricing?.subtracted_amount}
                      renderRawContent
                    />
                    <span className="label">
                      {`${translate("Price after discount")}`}:
                    </span>
                    <span className="offer-detail">
                      {FormatCurrency({
                        value: Number(paymentMethodPricing?.order_amount),
                        locale: locale
                      })}{" "}
                      {selectedOffer.periods !== 1 ? (
                        <span>
                          {"("}
                          {FormatCurrency({
                            value: Number(
                              paymentMethodPricing.order_monthly_amount
                            ),
                            locale: locale
                          })}{" "}
                          / {translate("month")}
                          {")"}
                        </span>
                      ) : null}
                    </span>
                  </>
                ) : discounted &&
                  currentPaymentMethod &&
                  currentPaymentMethod.canDiscount ? (
                  this.getDiscountSummary(selectedOffer)
                ) : null}
              </div>
            </div>
          </ContentSection>
          {hasSpecialOfferDescription || hasOfferDescription ? (
            <div>
              <ContentSection>
                <div
                  className={
                    "selected-offer-description selected-offer-description_first"
                  }
                  dangerouslySetInnerHTML={{
                    __html: getOfferDescription([
                      specialOfferDescription,
                      offerDescription
                    ])
                  }}
                />
                <Confirmation
                  selectedOffer={selectedOffer}
                  selectedPlanLoading={selectedPlanLoading}
                  selectedPlan={selectedPlan}
                  agreementAccepted={agreementAccepted}
                  setAgreementAccepted={this.setAgreementAccepted}
                  paymentMethodPricing={paymentMethodPricing}
                  paymentMethods={this.paymentMethods}
                  offerPaymentMethods={offerPaymentMethods}
                  paymentMethod={paymentMethod}
                  paymentMethodAlias={paymentMethodAlias}
                  currentPaymentMethod={currentPaymentMethod}
                  paymentDetails={paymentDetails}
                  setPaymentMethod={this.setPaymentMethod}
                  isRecurringPaymentMethod={isRecurringPaymentMethod}
                  currency={currency}
                  paymentFormLoading={paymentFormLoading}
                  cardModel={cardModel}
                  offerId={offerId}
                  finalizePayment={finalizePayment}
                  subscriptionId={subscriptionId}
                  small
                />
              </ContentSection>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}

export default compose(
  withAddAlert,
  withPaymentForm,
  withPaymentDetails,
  withGlobalConfigFields([globalConfigFields.OFFER_URL]),
  withUserData,
  withPlans
)(PaymentForm);
